import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider as DescopeAuthProvider } from '@descope/react-sdk';
import { AuthProvider } from './contexts/AuthContext';
import { SidebarProvider } from './contexts/SidebarContext';
import Layout from './components/layout/Layout';
import DealList from './components/DealList';
import DealDetail from './components/DealDetail';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import Settings from './pages/Settings';
import ProductMappings from './pages/LineItemMappings';
import Onboarding from './pages/Onboarding';
import HubSpotRoute from './components/HubSpotRoute';
import HubSpotDealDetail from './components/HubSpotDealDetail';
import HubSpotBillingDetail from './components/HubSpotBillingDetail';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30000,
    },
  },
});

// Get Descope project ID from environment
const descopeProjectId = import.meta.env.VITE_DESCOPE_PROJECT_ID;

/**
 * Detect at module load whether this page is a HubSpot iframe route.
 * If so, we skip Descope/AuthProvider entirely so they don't clobber the
 * iframe JWT getter that HubSpotRoute installs on apiClient.
 *
 * Checked via window.location.pathname (not react-router) because we need
 * the decision BEFORE rendering any providers.
 */
const isHubSpotIframeRoute =
  typeof window !== 'undefined' && window.location.pathname.startsWith('/hubspot/');

function App() {
  // HubSpot iframe routes bypass Descope and AuthProvider entirely.
  // AuthProvider's useEffect calls apiClient.setTokenGetter(null) when
  // Descope isn't authenticated, which would overwrite the JWT getter
  // that HubSpotRoute installs. Separating the trees avoids this conflict.
  if (isHubSpotIframeRoute) {
    return (
      <Router>
        <Routes>
          <Route
            path="/hubspot/deal/:dealId"
            element={
              <HubSpotRoute>
                <HubSpotDealDetail />
              </HubSpotRoute>
            }
          />
          <Route
            path="/hubspot/company/:recordId"
            element={
              <HubSpotRoute>
                <HubSpotBillingDetail recordType="company" />
              </HubSpotRoute>
            }
          />
          <Route
            path="/hubspot/contact/:recordId"
            element={
              <HubSpotRoute>
                <HubSpotBillingDetail recordType="contact" />
              </HubSpotRoute>
            }
          />
        </Routes>
      </Router>
    );
  }

  if (!descopeProjectId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <h2 className="text-red-800 text-lg font-semibold mb-2">Configuration Error</h2>
          <p className="text-red-600">
            VITE_DESCOPE_PROJECT_ID environment variable is not set.
            Please configure it in your .env file.
          </p>
        </div>
      </div>
    );
  }

  return (
    <DescopeAuthProvider projectId={descopeProjectId}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <SidebarProvider>
          <Router>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <DealList />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sync_deals/:dealId"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <DealDetail />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/product_mappings"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ProductMappings />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onboarding"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Onboarding />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Settings />
                    </Layout>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </Router>
          </SidebarProvider>
        </AuthProvider>
      </QueryClientProvider>
    </DescopeAuthProvider>
  );
}

export default App;
